import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Gift, Copy } from 'lucide-react';
import { adminApi } from '../lib/api';
import { formatPrice, formatDate } from '../lib/utils';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const STATUS_LABELS = {
  paid: { label: 'Payée', cls: 'bg-[#A5BBA8] text-white' },
  pending: { label: 'En attente de paiement', cls: 'bg-[#E8B25C] text-white' },
  released: { label: 'Place libérée', cls: 'bg-gray-200 text-gray-700' },
};

const Stat = ({ label, value, testId }) => (
  <div className="card-default">
    <p className="text-sm text-[#5C6A61]">{label}</p>
    <p className="text-2xl font-semibold text-[#2A312C] mt-1" data-testid={testId}>{value}</p>
  </div>
);

const RedemptionRow = ({ r }) => {
  const st = STATUS_LABELS[r.status] || STATUS_LABELS.released;
  return (
    <tr className="border-b border-[#E3DED5]" data-testid={`promo-row-${r.reservation_id}`}>
      <td className="py-3 px-4">
        <p className="font-medium text-[#2A312C]">{r.client_name || '-'}</p>
        <p className="text-xs text-[#5C6A61]">{r.client_email}</p>
      </td>
      <td className="py-3 px-4 text-[#5C6A61]">{r.date ? formatDate(r.date) : '-'}</td>
      <td className="py-3 px-4 text-[#5C6A61]">{r.reservation_id}</td>
      <td className="py-3 px-4 text-[#5C6A61]">{r.amount_before_discount != null ? formatPrice(r.amount_before_discount) : '-'}</td>
      <td className="py-3 px-4 text-[#C86B51]">{r.discount_amount ? `-${formatPrice(r.discount_amount)}` : '-'}</td>
      <td className="py-3 px-4 font-medium">{r.total_amount != null ? formatPrice(r.total_amount) : '-'}</td>
      <td className="py-3 px-4">
        <Badge className={st.cls}>{st.label}</Badge>
        {r.status === 'released' && r.release_reason && <p className="text-xs text-[#5C6A61] mt-1">{r.release_reason}</p>}
      </td>
    </tr>
  );
};

const ShareLink = () => {
  const link = `${window.location.origin}/?offre=decouverte`;
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success('Lien copié')).catch(() => toast.error('Copie impossible'));
  return (
    <div className="card-default flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid="promo-share">
      <div>
        <p className="font-medium text-[#2A312C]">Lien à partager (Comm'en Flandres)</p>
        <p className="text-sm text-[#5C6A61] break-all" data-testid="promo-share-link">{link}</p>
        <p className="text-xs text-[#5C6A61] mt-1">Ce lien ouvre la page d'accueil avec le bandeau. Il n'accorde aucun droit : l'éligibilité et la limite de 15 foyers restent vérifiées par le serveur.</p>
      </div>
      <Button variant="outline" onClick={copy} data-testid="promo-share-copy-btn">
        <Copy className="w-4 h-4 mr-2" /> Copier le lien
      </Button>
    </div>
  );
};

export default function PromoManagement() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.getPromo().then((r) => setData(r.data)).catch(() => toast.error("Impossible de charger l'offre"));
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    setSaving(true);
    try {
      await adminApi.setPromoActive(!data.campaign.active);
      toast.success(data.campaign.active ? 'Offre désactivée' : 'Offre activée');
      await load();
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-[#A5BBA8]" />;
  const c = data.campaign;

  return (
    <div className="space-y-6" data-testid="promo-management">
      <div className="card-default flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Gift className="w-6 h-6 text-[#C86B51] mt-1" />
          <div>
            <h2 className="font-semibold text-[#2A312C]">{c.label} — code {c.code} (-{c.percent} %)</h2>
            <p className="text-sm text-[#5C6A61]">Appliquée automatiquement aux nouveaux foyers éligibles, limitée à {c.max_uses} prestations payées.</p>
            {c.auto_disabled_at && !c.active && (
              <p className="text-sm text-[#C86B51] mt-1" data-testid="promo-auto-disabled">Désactivée automatiquement : les {c.max_uses} places ont été payées.</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={c.active ? 'bg-[#A5BBA8] text-white' : 'bg-gray-200 text-gray-700'} data-testid="promo-status-badge">
            {c.active ? 'Active' : 'Inactive'}
          </Badge>
          <Button onClick={toggle} disabled={saving} className={c.active ? 'bg-[#C86B51] hover:bg-[#b55d45]' : 'bg-[#2A312C] hover:bg-[#3a443d]'} data-testid="promo-toggle-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {c.active ? "Désactiver l'offre" : "Activer l'offre"}
          </Button>
        </div>
      </div>

      <ShareLink />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Offres utilisées (payées)" value={`${c.paid_count} / ${c.max_uses}`} testId="promo-paid-count" />
        <Stat label="Places disponibles" value={c.remaining} testId="promo-remaining" />
        <Stat label="Réductions accordées (payées)" value={formatPrice(data.total_discount_paid)} testId="promo-total-discount" />
      </div>
      {c.held_count > c.paid_count && (
        <p className="text-sm text-[#5C6A61]" data-testid="promo-pending-info">
          {c.held_count - c.paid_count} place(s) temporairement réservée(s) par un paiement en cours (libérée(s) automatiquement après 30 min sans paiement).
        </p>
      )}

      <div className="card-default overflow-x-auto">
        <h3 className="font-semibold text-[#2A312C] mb-4">Réservations ayant bénéficié de l'offre</h3>
        {data.redemptions.length === 0 ? (
          <p className="text-sm text-[#5C6A61]" data-testid="promo-empty">Aucune réservation pour le moment.</p>
        ) : (
          <table className="w-full text-sm" data-testid="promo-table">
            <thead>
              <tr className="text-left text-[#5C6A61] border-b border-[#E3DED5]">
                <th className="py-2 px-4">Client</th><th className="py-2 px-4">Prestation</th><th className="py-2 px-4">Réservation</th>
                <th className="py-2 px-4">Tarif initial</th><th className="py-2 px-4">Remise</th><th className="py-2 px-4">Payé</th><th className="py-2 px-4">Statut</th>
              </tr>
            </thead>
            <tbody>{data.redemptions.map((r) => <RedemptionRow key={r.redemption_id} r={r} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
